// Aura scan scheduler — pure helpers that turn a scan-timing mode plus the
// operator's caps into the gap (ms) inserted after each serial scan completes.
//
// Scheduling stays strictly serial (one scan in flight, frame captured at send
// time), so modes only change the *gap*. Kept dependency-free and Node-friendly
// so it's unit-testable and safe to import from both the browser hook and test/.

// Freshest-image mode still yields a beat to the browser/UI between scans.
const MAX_FLOOR_MS = 250;
const DEFAULT_SCAN_EVERY_S = 5;
// setTimeout's delay is internally a 32-bit signed int — anything beyond this
// overflows and fires almost immediately instead of respecting the delay.
// Matters now that SCAN EVERY accepts hours: an operator entering an
// extreme value (e.g. 1,000,000 hours) must not turn into a rapid-fire loop.
const MAX_GAP_MS = 2147483647;
const clampGap = (ms) => Math.min(ms, MAX_GAP_MS);

// Exponential moving average. Seeds on the first finite value; ignores
// non-finite samples (a timeout or missing usage isn't a real measurement).
export function emaUpdate(prev, value, alpha = 0.3) {
  if (!Number.isFinite(value)) return prev;
  if (prev == null || !Number.isFinite(prev)) return value;
  return prev + alpha * (value - prev);
}

// The `interval` gap: fixed SCAN EVERY seconds (fractional allowed — the
// operator enters a number + unit, e.g. 1s, 5m, 12h), defaulting when
// unparseable.
function intervalGapMs(scanEvery) {
  const secs = parseFloat(scanEvery);
  return clampGap(
    (Number.isFinite(secs) && secs > 0 ? secs : DEFAULT_SCAN_EVERY_S) * 1000,
  );
}

// Compute the post-scan gap (ms) for the given mode.
//   knobs  — { scanEvery, budgetPerHour, networkMbPerHour, inputRate, outputRate }
//   sample — per-session EMAs { promptTokens, completionTokens, bytes, durationMs,
//            scanCost } — scanCost ($ per scan) is measured directly when the
//            engine isn't billed per token (DECISION: per predict-second)
//
// interval → fixed scanEvery. max → 250 ms floor. budget → derived from the
// spend/data caps, most-restrictive-wins (below). Unknown modes fall back to
// interval so a bad setting can never stall the loop.
export function computeGapMs(mode, knobs = {}, sample = {}) {
  if (mode === "max") return MAX_FLOOR_MS;
  if (mode !== "budget") return intervalGapMs(knobs.scanEvery);

  const { bytes, durationMs } = sample;
  const promptTokens = sample.promptTokens ?? sample.tokens;
  // Legacy callers provide one combined token EMA; a missing modern
  // completion sample must stay missing so bootstrap can still fall back.
  const completionTokens = sample.completionTokens ?? (Number.isFinite(sample.tokens) ? 0 : null);
  const dur = Number.isFinite(durationMs) ? durationMs : 0;
  const haveTokens = Number.isFinite(promptTokens) || Number.isFinite(completionTokens);
  const haveBytes = Number.isFinite(bytes);
  const haveScanCost = Number.isFinite(sample.scanCost) && sample.scanCost > 0;

  // Bootstrap: no measurement of either dimension yet → behave like interval.
  if (!haveTokens && !haveBytes && !haveScanCost) return intervalGapMs(knobs.scanEvery);

  const gaps = [];

  // Cost cap: needs a token sample, both $/1M rates, and a positive budget.
  // A provider that never returns usage or a free local model simply disables
  // this cap. `rate` remains a compatibility fallback for callers upgraded
  // before the separate input/output rates were introduced.
  const inputRate = Number(knobs.inputRate ?? knobs.rate);
  const outputRate = Number(knobs.outputRate ?? knobs.rate);
  const budgetPerHour = Number(knobs.budgetPerHour);
  if (haveScanCost && budgetPerHour > 0) {
    gaps.push(Math.max(0, (3600e3 * sample.scanCost) / budgetPerHour - dur));
  } else if (haveTokens && inputRate >= 0 && outputRate >= 0 && (inputRate > 0 || outputRate > 0) && budgetPerHour > 0) {
    const costPerScan = ((Number(promptTokens) || 0) * inputRate + (Number(completionTokens) || 0) * outputRate) / 1e6;
    gaps.push(Math.max(0, (3600e3 * costPerScan) / budgetPerHour - dur));
  }

  // Network cap: needs a payload-byte sample and a positive MB/hour cap
  // (blank/0 = off). Measured client-side, so it works even with no usage.
  const mbPerHour = Number(knobs.networkMbPerHour);
  if (haveBytes && mbPerHour > 0) {
    gaps.push(Math.max(0, (3600e3 * bytes) / (mbPerHour * 1e6) - dur));
  }

  // No active cap (e.g. no usage data and no MB cap) → can't derive a cadence,
  // so fall back to interval behavior.
  if (gaps.length === 0) return intervalGapMs(knobs.scanEvery);

  return clampGap(Math.max(...gaps));
}
